// UX-Vorschlag P3: Adapter „Schritte" ueber zwei getrennte Servermodelle.
//   Projektaufgabe   → Checkliste (/api/tasks/:id/checklist); Befoerdern legt
//                      eine Sub-Aufgabe in derselben Phase an und entfernt den
//                      Checklistenpunkt.
//   Persoenliche A.  → Schritte (verschachtelte personal_tasks); Befoerdern ist
//                      der vorhandene Server-Weg /promote.
// Keine Migration: beide Seiten behalten ihr Modell, classic sieht nichts Neues.

import type { PersonalTaskStep, Task, TaskChecklistItem } from "@juuplan/shared";
import { api } from "./api";
import {
  addChecklistItem,
  deleteChecklistItem,
  fetchChecklist,
  patchChecklistItem,
} from "../store/checklist";
import { addStep, deleteStep, fetchSteps, promoteStep, updateStep } from "../store/personalTasks";
import type { TaskRecord } from "./taskRecord";
import type { DetailStep } from "./taskDetailModel";

export interface StepsAdapter {
  load(): Promise<DetailStep[]>;
  add(title: string): Promise<void>;
  setDone(step: DetailStep, done: boolean): Promise<void>;
  remove(step: DetailStep): Promise<void>;
  /** Projekt: Sub-Aufgabe; persoenlich: eigene Aufgabe in derselben Liste. */
  promote(step: DetailStep): Promise<void>;
  promoteLabel: string;
}

export function checklistToSteps(items: TaskChecklistItem[]): DetailStep[] {
  return [...items]
    .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
    .map((i) => ({ id: i.id, title: i.title, done: i.is_done, canPromote: true }));
}

export function personalStepsToSteps(steps: PersonalTaskStep[]): DetailStep[] {
  return steps.map((s) => ({
    id: s.id,
    title: s.linked_info?.title ?? s.title,
    done: s.linked_info ? s.linked_info.is_done === true : s.is_done,
    canPromote: !s.linked_task_id && !s.linked_personal_task_id,
  }));
}

export function stepsAdapterFor(record: TaskRecord): StepsAdapter {
  if (record.source === "project") {
    const taskId = record.id;
    const task = record.raw as Task;
    return {
      promoteLabel: "Zur Sub-Aufgabe machen",
      load: async () => checklistToSteps(await fetchChecklist(taskId)),
      add: async (title) => {
        await addChecklistItem(taskId, title);
      },
      setDone: async (s, done) => {
        await patchChecklistItem(taskId, s.id, { is_done: done });
      },
      remove: (s) => deleteChecklistItem(taskId, s.id),
      promote: async (s) => {
        // Erst anlegen, dann den Punkt entfernen: scheitert das Anlegen, geht
        // nichts verloren.
        await api<Task>(`/api/phases/${task.phase_id}/tasks`, {
          method: "POST",
          body: { title: s.title, parent_id: taskId },
        });
        await deleteChecklistItem(taskId, s.id);
      },
    };
  }
  const taskId = record.id;
  return {
    promoteLabel: "Zur eigenen Aufgabe machen",
    load: async () => personalStepsToSteps(await fetchSteps(taskId)),
    add: async (title) => {
      await addStep(taskId, title);
    },
    setDone: async (s, done) => {
      await updateStep(s.id, { is_done: done });
    },
    remove: (s) => deleteStep(s.id),
    promote: async (s) => {
      await promoteStep(s.id);
    },
  };
}

/** Beschreibung speichern — im Format des jeweiligen Typs (siehe taskDetailModel). */
export function saveDescription(record: TaskRecord, value: string): Promise<unknown> {
  if (record.source === "project") {
    return api(`/api/tasks/${record.id}`, { method: "PATCH", body: { description: value || null } });
  }
  return api(`/api/personal-tasks/${record.id}`, { method: "PATCH", body: { notes: value || null } });
}
